import { create } from "zustand";
import type { User, Farm } from "@/types/models";
import { authService } from "@/services/authService";
import { clearStaleApiCache } from "@/lib/apiCache";

interface AuthState {
  user: User | null;
  farm: Farm | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // OTP state
  otpRequired: boolean;
  otpToken: string | null;
  emailHint: string | null;

  login: (phone: string, pin: string) => Promise<void>;
  verifyOTP: (code: string) => Promise<void>;
  clearOTP: () => void;
  logout: () => void;
  loadSession: () => Promise<void>;
  setFarm: (farm: Farm) => void;
  changePIN: (currentPin: string, newPin: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  farm: null,
  token: localStorage.getItem("accessToken"),
  isAuthenticated: false,
  isLoading: true,
  error: null,

  otpRequired: false,
  otpToken: null,
  emailHint: null,

  login: async (phone: string, pin: string) => {
    set({ isLoading: true, error: null, otpRequired: false, otpToken: null, emailHint: null });
    try {
      const data = await authService.login(phone, pin);

      if (data.otp_required) {
        set({
          otpRequired: true,
          otpToken: data.otp_token ?? null,
          emailHint: data.email_hint ?? null,
          isLoading: false,
        });
        return;
      }

      localStorage.setItem("accessToken", data.token!);
      set({
        user: data.user ?? null,
        farm: data.farm ?? null,
        token: data.token ?? null,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Login failed";
      // Try to extract backend error message
      const axiosErr = err as { response?: { data?: { error?: string } } };
      const serverMessage = axiosErr?.response?.data?.error;
      set({
        error: serverMessage || message,
        isLoading: false,
      });
      throw err;
    }
  },

  verifyOTP: async (code: string) => {
    const { otpToken } = get();
    if (!otpToken) return;

    set({ isLoading: true, error: null });
    try {
      const data = await authService.verifyOTP(otpToken, code);
      localStorage.setItem("accessToken", data.token!);
      set({
        user: data.user ?? null,
        farm: data.farm ?? null,
        token: data.token ?? null,
        isAuthenticated: true,
        isLoading: false,
        otpRequired: false,
        otpToken: null,
        emailHint: null,
      });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Verification failed";
      const axiosErr = err as { response?: { data?: { error?: string } } };
      const serverMessage = axiosErr?.response?.data?.error;
      set({
        error: serverMessage || message,
        isLoading: false,
      });
      throw err;
    }
  },

  clearOTP: () => {
    set({
      otpRequired: false,
      otpToken: null,
      emailHint: null,
      error: null,
    });
  },

  // Changing the PIN ends every older session, this one included, so the
  // response carries the token that replaces it. Dropping it would sign the
  // person out on their next request.
  changePIN: async (currentPin, newPin) => {
    const data = await authService.changePIN(currentPin, newPin);
    if (data?.token) {
      localStorage.setItem("accessToken", data.token);
      set({ token: data.token });
      return;
    }
    // The PIN changed but no replacement came back (a frontend and backend
    // from different deploys). The old token is dead now, so end the session
    // here rather than let the next request discover it.
    get().logout();
  },

  logout: () => {
    localStorage.removeItem("accessToken");
    clearStaleApiCache();
    set({
      user: null,
      farm: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      otpRequired: false,
      otpToken: null,
      emailHint: null,
    });
  },

  loadSession: async () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      set({ isLoading: false, isAuthenticated: false });
      return;
    }

    try {
      const data = await authService.me();
      set({
        user: data.user,
        farm: data.farm,
        token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401) {
        // Genuine auth rejection — the token is invalid/expired, clear it.
        // (Only 401. A 403 means the token is valid but lacks permission, and a
        // network/5xx error means we couldn't verify it — neither should nuke a
        // good session, which is the whole point of this fix.)
        localStorage.removeItem("accessToken");
        set({
          user: null,
          farm: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
      } else {
        // Network / server error (backend restarting, flaky connection). Do NOT
        // delete a valid token — that would strand the user on sign-in needing a
        // fresh emailed OTP just to get back in. Keep it so reloading once
        // connectivity returns recovers the session.
        set({ isLoading: false });
      }
    }
  },

  setFarm: (farm: Farm) => set({ farm }),
}));
